import React, { useState, useEffect, useCallback, useRef } from 'react';
import { executeFunction } from '../../../Assistant/functionCalls';
import axios from 'axios';
import { fetchWorkspaceSources, runWorkspaceWebSearch, searchWorkspaceVectorStore, getWorkerApiUrl } from '../../../../api';
import useSessionContext from '../../../../hooks/useSessionContext';
import { MagnifyingGlassIcon, XMarkIcon, BookOpenIcon, CheckCircleIcon, InformationCircleIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { SOURCE_RELEVANCE_MIN, rankAndFilterSources, formatRelevanceScore } from './sourceRelevance';

// Helper logging function
const isLocalhost = () => {
  return typeof window !== 'undefined' && 
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
};

const log = (...args) => {
  if (isLocalhost()) {
    console.log('[SourcesSearch]', ...args);
  }
};

const SourcesSearch = ({ 
  articleContext, 
  sources = [], 
  onSourceSelect, 
  triggerSearch, 
  language = 'english',
  onSearchComplete = () => {},
  onClose = () => {},
  multiSelect = false,
  maxSources = 1,
  layout = 'cards',
  hideHeader = false,
  hideCloseButton = false
}) => {
  const { t } = useTranslation();
  const { token, workspaceId } = useSessionContext();
  // Initialize state from localStorage if available
  const [suggestedSources, setSuggestedSources] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQueries, setSearchQueries] = useState([]);
  const [customQuery, setCustomQuery] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSourceForDrawer, setSelectedSourceForDrawer] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [, setTotalResults] = useState(0);
  const [rejectedBelowThreshold, setRejectedBelowThreshold] = useState(0);
  const [resultsPerPage] = useState(20); // Results per page
  const [maxResultsPerQuery] = useState(50); // Max results per single query
  
  // API Sources configuration.
  // NCBI is executed in-browser as a static function; every other entry is a
  // workspace dataset and runs server-side, because workspace-scoped datasets
  // are not resolvable from the browser's user-scoped Directus query.
  const NCBI_SOURCE = {
    id: 'ncbi',
    name: 'NCBI PubMed',
    description: 'Scientific articles and research papers',
    enabled: true,
    runtime: 'builtin',
    searchFunction: 'search_ncbi_articles'
  };

  const [selectedApiSources, setSelectedApiSources] = useState(['ncbi']);
  const [availableApiSources, setAvailableApiSources] = useState([NCBI_SOURCE]);
  const [vectorStoreFiles, setVectorStoreFiles] = useState([]);
  const [vectorStoreFilter, setVectorStoreFilter] = useState('');
  const [activeTab, setActiveTab] = useState('recommendations');
  // Weak matches are hidden by default but never discarded — this toggles them.
  const [showWeakMatches, setShowWeakMatches] = useState(false);
  const [isWebSearching, setIsWebSearching] = useState(false);
  const [webSearchError, setWebSearchError] = useState(null);
  
  // Use a counter to track search attempts
  const searchAttemptRef = useRef(0);
  
  // Use a ref to track the last trigger value we've responded to
  const lastTriggerRef = useRef(0);

  // Log component props when they change
  useEffect(() => {
    log('Component mounted/updated with props:', { 
      articleContext: articleContext ? articleContext.substring(0, 50) + '...' : null, 
      sourcesCount: sources.length,
      triggerSearch,
      lastTrigger: lastTriggerRef.current,
      suggestedSourcesCount: suggestedSources.length
    });
  }, [articleContext, sources, triggerSearch, suggestedSources.length]);

  // Helper function to extract search queries from context
  const generateSearchQueries = (context) => {
    const queries = [];
    
    // Polish to English medical/scientific terms mapping
    const polishToEnglish = {
      'adhd': 'ADHD attention deficit hyperactivity disorder',
      'dzieci': 'children pediatric',
      'dorosły': 'adults',
      'dorośli': 'adults',
      'dorosłych': 'adults', 
      'doros': 'adults',
      'dysleksja': 'dyslexia reading disorder',
      'autyzm': 'autism spectrum disorder',
      'zespół aspergera': 'asperger syndrome',
      'metody': 'methods treatment therapy',
      'leczenie': 'treatment therapy',
      'terapia': 'therapy treatment',
      'badania': 'research studies',
      'medycyna': 'medicine medical',
      'zdrowie': 'health wellness',
      'choroba': 'disease disorder',
      'syndrom': 'syndrome',
      'zaburzenie': 'disorder',
      'zaburzenia': 'disorders',
      'dieta': 'diet nutrition',
      'odżywianie': 'nutrition diet',
      'sport': 'exercise physical activity',
      'trening': 'training exercise',
      'fitness': 'fitness exercise',
      'suplementy': 'supplements',
      'witaminy': 'vitamins',
      'minerały': 'minerals',
      'białko': 'protein',
      'węglowodany': 'carbohydrates',
      'tłuszcze': 'fats lipids',
      'cukrzyca': 'diabetes',
      'nadciśnienie': 'hypertension',
      'serce': 'heart cardiac',
      'mózg': 'brain neurological',
      'depresja': 'depression',
      'lęk': 'anxiety',
      'stres': 'stress',
      'sen': 'sleep',
      'bezsenność': 'insomnia sleep disorders',
      'covid': 'COVID-19 coronavirus',
      'szczepienia': 'vaccination vaccines',
      'odporność': 'immunity immune system',
      'rak': 'cancer oncology',
      'nowotwór': 'tumor cancer',
      'psychologia': 'psychology psychological',
      'psychiatria': 'psychiatry psychiatric',
      
      // Technology and interface terms
      'interfejs': 'interface user interface',
      'interfejsy': 'interfaces user interfaces',
      'interaktywny': 'interactive',
      'interaktywne': 'interactive',
      'konwersacyjny': 'conversational',
      'konwersacyjne': 'conversational',
      'projektowanie': 'design designing',
      'designin': 'designing', // Fix typo
      'inteactive': 'interactive', // Fix typo
      'technologia': 'technology',
      'technologie': 'technologies',
      'aplikacja': 'application software',
      'aplikacje': 'applications software',
      'oprogramowanie': 'software',
      'system': 'system',
      'systemy': 'systems',
      'komputery': 'computers',
      'komputer': 'computer',
      'sztuczna inteligencja': 'artificial intelligence AI',
      'uczenie maszynowe': 'machine learning',
      'ai': 'artificial intelligence',
      'ml': 'machine learning',
      'chatbot': 'chatbot conversational agent',
      'chatboty': 'chatbots conversational agents',
      'bot': 'bot agent',
      'asystent': 'assistant',
      'wirtualny': 'virtual',
      'cyfrowy': 'digital',
      'online': 'online digital',
      'internet': 'internet web',
      'www': 'web internet',
      'strona': 'website page',
      'witryna': 'website',
      
      // Time and century terms
      'xxi': '21st century',
      'xx': '20th century', 
      'wiek': 'century',
      'wieku': 'century',
      'stulecie': 'century',
      '21': '21st century',
      '20': '20th century',
      
      // Common words and connectors
      'oraz': 'and',
      'albo': 'or',
      'ale': 'but',
      'jednak': 'however',
      'dlatego': 'therefore',
      'ponieważ': 'because',
      'gdy': 'when',
      'jeśli': 'if',
      'czy': 'whether',
      'lub': 'or',
      'tak': 'yes',
      'nie': 'no not',
      'przez': 'through by',
      'między': 'between',
      'podczas': 'during',
      'przed': 'before',
      'po': 'after',
      'około': 'about approximately',
      'wszystkie': 'all',
      'każdy': 'every each',
      'inne': 'other',
      'główny': 'main primary',
      'ważny': 'important',
      'nowy': 'new',
      'stary': 'old'
    };
    
    // Function to translate and clean search terms
    const translateToEnglishQuery = (text) => {
      let translatedText = text.toLowerCase();
      
      // Replace Polish terms with English equivalents
      Object.entries(polishToEnglish).forEach(([polish, english]) => {
        const regex = new RegExp(`\\b${polish}\\b`, 'gi');
        translatedText = translatedText.replace(regex, english);
      });
      
      // Remove common Polish words that don't translate well
      const polishStopWords = ['i', 'w', 'na', 'z', 'ze', 'do', 'od', 'po', 'dla', 'jak', 'co', 'się', 'jest', 'są', 'było', 'będzie', 'może', 'tylko', 'już', 'także', 'również', 'bardzo', 'więcej', 'mniej', 'lepsze', 'najlepsze', 'gorsze', 'najgorsze', 'nowe', 'stare', 'tego', 'tej', 'ten', 'ta', 'to', 'te', 'tym', 'tych', 'które', 'która', 'który', 'oraz', 'lub', 'albo', 'ani', 'czy'];
      polishStopWords.forEach(word => {
        const regex = new RegExp(`\\b${word}\\b`, 'gi');
        translatedText = translatedText.replace(regex, '');
      });
      
      // Additional cleaning: fix common typos and normalize
      translatedText = translatedText
        .replace(/\b(xxI|xxi|XXi)\b/gi, '21st century') // Fix XXI variations
        .replace(/\bdesignin\b/gi, 'designing') // Fix typo
        .replace(/\binteactive\b/gi, 'interactive') // Fix typo
        .replace(/\bconversational\s+interfaces?\b/gi, 'conversational interface human computer interaction') // Add HCI terms
        .replace(/\binteractive\s+interfaces?\b/gi, 'interactive interface user experience UX');
      
      // Clean up extra spaces and punctuation
      translatedText = translatedText
        .replace(/[^\w\s]/g, ' ') // Remove punctuation
        .replace(/\s+/g, ' ') // Multiple spaces to single space
        .trim();
      
      return translatedText;
    };
    
    // Check if context appears to be in Polish
    const isPolish = /[ąćęłńóśźż]/.test(context) || 
                    /(dzieci|dorosły|metody|leczenie|zdrowie|badania)/.test(context.toLowerCase());
    
    if (isPolish) {
      log('Detected Polish context, translating to English for NCBI search');
      
      // Translate the entire context
      const translatedContext = translateToEnglishQuery(context);
      log('Translated context:', translatedContext);
      
      // Generate queries from translated context
      const words = translatedContext.split(' ').filter(word => word.length > 2);
      
      // Create focused search queries
      if (words.length >= 4) {
        // Take different combinations of words
        queries.push(words.slice(0, 3).join(' '));
        queries.push(words.slice(1, 4).join(' '));
        if (words.length >= 6) {
          queries.push(words.slice(2, 5).join(' '));
        }
      } else {
        queries.push(translatedContext);
      }
    } else {
      // English context - use existing logic
      const sentences = context.split(/[.!?]+/).filter(s => s.trim().length > 10);
      const selectedSentences = sentences.slice(0, 3);
      
      selectedSentences.forEach(sentence => {
        let query = sentence.trim()
          .replace(/^(when|where|how|why|what|which|who)\s+/i, '')
          .replace(/['"]/g, '')
          .replace(/\s+/g, ' ')
          .substring(0, 100);
        
        if (query.length > 15) {
          queries.push(query);
        }
      });
    }
    
    // If no good queries found, create a basic one
    if (queries.length === 0) {
      const basicQuery = isPolish ? translateToEnglishQuery(context.substring(0, 100)) : context.substring(0, 100);
      queries.push(basicQuery);
    }
    
    log('Generated search queries:', queries);
    return queries.slice(0, 3); // Maximum 3 queries
  };

  // Load what this workspace can actually search, rather than assuming NCBI.
  useEffect(() => {
    let cancelled = false;
    if (!token) return undefined;

    (async () => {
      try {
        const inventory = await fetchWorkspaceSources(token, workspaceId);
        if (cancelled) return;

        const workspaceTools = inventory.tools
          .filter((tool) => tool.id !== NCBI_SOURCE.id)
          .map((tool) => ({
            id: tool.id,
            name: tool.name,
            description: tool.description,
            enabled: true,
            runtime: 'workspace',
            searchFunction: tool.id
          }));

        const allSources = [NCBI_SOURCE, ...workspaceTools];
        setAvailableApiSources(allSources);
        // Search everything the workspace can actually search, not just NCBI.
        setSelectedApiSources(allSources.map((source) => source.id));
        setVectorStoreFiles(inventory.vectorStore.files);
        log('SOURCE FLOW: workspace inventory loaded', {
          tools: workspaceTools.length,
          files: inventory.vectorStore.files.length
        });
      } catch (err) {
        // Keep the built-in source usable if the inventory call fails.
        log('SOURCE FLOW: workspace inventory failed', err.message);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- NCBI_SOURCE is a stable literal
  }, [token, workspaceId]);

  // Workspace datasets run server-side; see NCBI_SOURCE comment above.
  const searchWorkspaceTool = async (apiSource, query) => {
    try {
      const response = await axios.post(
        getWorkerApiUrl('/workspace/tools/search'),
        { toolName: apiSource.searchFunction, query, workspaceId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const sources = Array.isArray(response.data?.data?.sources) ? response.data.data.sources : [];
      return {
        success: sources.length > 0,
        sources,
        query,
        apiSource: apiSource.id,
        error: sources.length === 0 ? (response.data?.data?.error || 'No results found') : null
      };
    } catch (error) {
      log('SOURCE FLOW: workspace tool search failed', apiSource.id, error.message);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Search failed',
        sources: [],
        query,
        apiSource: apiSource.id
      };
    }
  };

  const handleWebSearch = async () => {
    const query = String(customQuery || articleContext || '').trim();
    if (!query) {
      setWebSearchError(t('articles.create.articleSources.webSearchNeedsQuery', 'Enter a search query first.'));
      return;
    }

    setIsWebSearching(true);
    setWebSearchError(null);
    try {
      const result = await runWorkspaceWebSearch(token, query);

      // Merge into the main table so web hits are scored, sorted and filtered
      // alongside everything else instead of living in their own list.
      if (result.sources.length > 0) {
        const merged = Array.from(new Map(
          [...suggestedSources, ...result.sources].map((source) => [source.id || source.url, source])
        ).values());
        const ranked = rankAndFilterSources(
          merged,
          `${articleContext || ''} ${query}`.trim(),
          [query],
          SOURCE_RELEVANCE_MIN
        );
        setSuggestedSources(ranked.all);
        setRejectedBelowThreshold(ranked.rejected);
        setActiveTab('recommendations');
      }

      if (result.sources.length === 0) {
        setWebSearchError(t(
          'articles.create.articleSources.webSearchEmpty',
          'No results from the verified sources list.'
        ));
      }
    } catch (err) {
      const message = err.response?.status === 503
        ? t('articles.create.articleSources.webSearchDisabled', 'Web search is not enabled.')
        : (err.response?.data?.error || err.message);
      setWebSearchError(message);
    } finally {
      setIsWebSearching(false);
    }
  };

  // Generic API search function that can handle multiple sources
  const searchApiSources = async (apiSourceId, query, maxResults = 5) => {
    log('SOURCE FLOW: searchApiSources called with:', { apiSourceId, query, maxResults });
    
    const apiSource = availableApiSources.find(source => source.id === apiSourceId);
    log('SOURCE FLOW: Found apiSource:', apiSource);
    
    if (!apiSource || !apiSource.enabled) {
      log('SOURCE FLOW: API source not available or disabled:', { apiSourceId, found: !!apiSource, enabled: apiSource?.enabled });
      return {
        success: false,
        error: `API source ${apiSourceId} not available`,
        sources: [],
        query: query
      };
    }

    log('SOURCE FLOW: Searching', apiSource.name, 'with query:', query, 'maxResults:', maxResults);

    if (apiSource.runtime === 'workspace') {
      return searchWorkspaceTool(apiSource, query);
    }

    try {
      const functionCall = {
        name: apiSource.searchFunction,
        arguments: JSON.stringify({
          query: query,
          max_results: maxResults,
          search_field: 'all'
        })
      };
      
      log('SOURCE FLOW: Calling executeFunction with:', functionCall);
      const result = await executeFunction(functionCall);
      log('SOURCE FLOW:', apiSource.name, 'search result:', result);
      
      if (result.success && result.articles && result.articles.length > 0) {
        // Transform articles to standard format - different APIs may have different structures
        const transformedSources = result.articles.map(article => ({
          id: article.pmcid || article.pmid || article.id || `${apiSourceId}-${Date.now()}-${Math.random()}`,
          title: article.title,
          authors: article.authors,
          journal: article.journal,
          pubDate: article.pub_date || article.pubDate || article.published_date,
          doi: article.doi,
          url: article.abstract_url || article.url,
          summary: article.summary || article.title,
          abstract: article.abstract || 'Abstract not available',
          fullText: article.fullText || '',
          source: apiSource.name,
          query: query,
          apiSource: apiSourceId, // Track which API source this came from
          type: apiSourceId === 'ncbi' ? 'ncbi' : 'user_selected'
        }));
        
        return {
          success: true,
          sources: transformedSources,
          query: query,
          totalFound: result.total_found,
          apiSource: apiSourceId
        };
      } else {
        return {
          success: false,
          error: result.message || 'No articles found',
          sources: [],
          query: query,
          apiSource: apiSourceId
        };
      }
    } catch (error) {
      log('SOURCE FLOW: Error in', apiSource.name, 'search:', error);
      return {
        success: false,
        error: error.message || 'Search failed',
        sources: [],
        query: query,
        apiSource: apiSourceId
      };
    }
  };


  // Function to perform multiple searches with different queries
  /**
   * Fans a set of queries across everything the user ticked, in the same order
   * generation uses: workspace documents first, then the checked datasets.
   * `advanced` applies PubMed field syntax, which only NCBI understands.
   */
  const collectSourcesForQueries = async (queries, { advanced = false, maxResults = 5 } = {}) => {
    // Workspace documents and each ticked dataset are searched in parallel.
    // Queries against one source stay sequential: NCBI without an API key
    // allows ~3 requests/s and answers bursts with 429.

    // Workspace documents: run every query variant and keep each file's
    // best score: the generated queries are English-translated, and an English
    // query scores a Polish-topic match far higher against English PDFs.
    const searchVectorStore = async () => {
      if (!token) return [];
      const bestByFile = new Map();
      const variants = queries.slice(0, 3).filter(Boolean);
      const results = await Promise.all(variants.map(async (query) => {
        try {
          return await searchWorkspaceVectorStore(token, query, workspaceId);
        } catch (err) {
          log('SOURCE FLOW: vector store search failed', err.message);
          return [];
        }
      }));
      results.flat().forEach((hit) => {
        const existing = bestByFile.get(hit.id);
        if (!existing || Number(hit.relevanceScore) > Number(existing.relevanceScore)) {
          bestByFile.set(hit.id, hit);
        }
      });
      log('SOURCE FLOW: vector store hits', bestByFile.size);
      return [...bestByFile.values()];
    };

    const searchOneApiSource = async (apiSourceId) => {
      const apiSource = availableApiSources.find((source) => source.id === apiSourceId);
      const found = [];
      for (const query of queries) {
        // PubMed field syntax would be nonsense for the registry APIs.
        const effectiveQuery = advanced && apiSource?.runtime === 'builtin'
          ? constructAdvancedQuery(query)
          : query;
        try {
          const result = await searchApiSources(apiSourceId, effectiveQuery, maxResults);
          if (result.sources?.length > 0) found.push(...result.sources);
          log(`SOURCE FLOW: "${effectiveQuery}" via ${apiSourceId} -> ${result.sources?.length || 0}`);
        } catch (err) {
          log(`SOURCE FLOW: error searching "${query}" in ${apiSourceId}:`, err);
        }
      }
      return found;
    };

    // Workspace documents stay first in the merged list, as before.
    const groups = await Promise.all([
      searchVectorStore(),
      ...selectedApiSources.map((apiSourceId) => searchOneApiSource(apiSourceId))
    ]);
    const collected = groups.flat();

    return Array.from(new Map(collected.map((source) => [source.id, source])).values());
  };

  const performSearch = useCallback(async () => {
    log('SOURCE FLOW: performSearch called');
    if (!articleContext || articleContext.trim().length < 5) {
      log('SOURCE FLOW: Article context too short, skipping search');
      onSearchComplete(false);
      return;
    }
    
    setIsLoading(true);
    setError('');
    log('SOURCE FLOW: Starting search with article context:', articleContext.substring(0, 50) + '...');

    // Clear previous results when starting new search
    setSuggestedSources([]);
    setCurrentPage(1);
    setTotalResults(0);
    setRejectedBelowThreshold(0);

    try {
      // Generate search queries from context
      const queries = generateSearchQueries(articleContext);
      log('SOURCE FLOW: Generated queries:', queries);
      setSearchQueries(queries); // Replace previous queries
      
      const uniqueSources = await collectSourcesForQueries(queries, {
        maxResults: maxResultsPerQuery
      });

      log('SOURCE FLOW: Total unique sources found:', uniqueSources.length);

      const shouldFilterByScore = layout === 'table' || multiSelect;
      const ranked = rankAndFilterSources(uniqueSources, articleContext, queries, SOURCE_RELEVANCE_MIN);

      // Show every candidate with its score rather than hiding weak matches;
      // the user judges them. Card layouts keep the stricter behaviour.
      const visible = shouldFilterByScore ? ranked.all : ranked.kept;
      setRejectedBelowThreshold(ranked.rejected);

      if (visible.length > 0) {
        setSuggestedSources(visible);
        setTotalResults(visible.length);
        onSearchComplete(ranked.kept.length > 0, ranked.kept.length);
      } else {
        setSuggestedSources([]);
        setTotalResults(0);
        setError(t(
          'articles.create.articleSources.noResults',
          'No sources found for this topic. Try another phrase or tick more sources.'
        ));
        onSearchComplete(false);
      }
      
    } catch (err) {
      log('SOURCE FLOW: Unexpected error in performSearch:', err.message);
      console.error('Error in performSearch:', err);
      setError('An unexpected error occurred while searching for sources');
      onSearchComplete(false);
    } finally {
      setIsLoading(false);
      log('SOURCE FLOW: Search process completed');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- internal search helpers are defined in component scope
  }, [articleContext, onSearchComplete]);

  // Effect to track changes to the triggerSearch prop
  useEffect(() => {
    log('triggerSearch changed:', triggerSearch, 'lastTrigger:', lastTriggerRef.current);
    
    // Only perform search if triggerSearch value has increased
    if (triggerSearch > lastTriggerRef.current) {
      log('SOURCE FLOW: New search triggered with timestamp:', triggerSearch);
      lastTriggerRef.current = triggerSearch;
      performSearch();
    }
  }, [triggerSearch, performSearch]);

  // Update filtering logic to allow partial matches and include abstract
  // This function is no longer used after changes to handleCustomSearch.
  /*
  const filterResults = (sources, query) => {
    const lowerCaseQuery = query.toLowerCase();
    return sources.filter(source =>
      source.title.toLowerCase().includes(lowerCaseQuery) ||
      (source.abstract && source.abstract.toLowerCase().includes(lowerCaseQuery))
    );
  };
  */

  // Adjust query construction and experiment with different search fields
  const constructAdvancedQuery = (query) => {
    // Test with different fields like Title, Abstract, or Keywords
    return `(${query})[Title] OR (${query})[Abstract] OR (${query})[Keywords]`;
  };

  // Use enhanced query construction in custom search
  const handleCustomSearch = async () => {
    if (!customQuery.trim()) {
      setError('Please enter a specific search query.');
      return;
    }
    
    setIsLoading(true);
    setError('');
    // Initialize/clear states for a new custom search to prevent stale data display
    setSuggestedSources([]); 
    setSearchQueries([]);

    try {
      const rawQuery = customQuery.trim();
      log('Custom Search: fanning out across checked sources:', selectedApiSources);

      // Include the translated variants: a Polish phrase scores poorly against
      // English PDFs and English-indexed databases.
      const queryVariants = Array.from(new Set([rawQuery, ...generateSearchQueries(rawQuery)]))
        .filter(Boolean)
        .slice(0, 3);

      // Hits every ticked dataset (and the workspace documents), not just NCBI.
      const uniqueSources = await collectSourcesForQueries(queryVariants, {
        advanced: true,
        maxResults: 30
      });

      if (uniqueSources.length > 0) {
        const shouldFilterByScore = layout === 'table' || multiSelect;
        const ranked = rankAndFilterSources(
          uniqueSources,
          `${articleContext || ''} ${rawQuery}`.trim(),
          [rawQuery],
          SOURCE_RELEVANCE_MIN
        );
        setRejectedBelowThreshold(ranked.rejected);
        setSuggestedSources(shouldFilterByScore ? ranked.all : ranked.kept);
        setSearchQueries([rawQuery]);
        setError('');
        log('Custom Search: scored sources:', ranked.all.length);
      } else {
        setSuggestedSources([]);
        setSearchQueries([]);
        setError(t(
          'articles.create.articleSources.noResults',
          'No sources found for this topic. Try another phrase or tick more sources.'
        ));
        log('Custom Search: no sources from any checked provider');
      }
    } catch (err) {
      log('Custom Search: Unexpected error:', err.message);
      setError(`An unexpected error occurred during custom search: ${err.message}`);
      setSuggestedSources([]); // Clear on error
      setSearchQueries([]);   // Clear on error
    } finally {
      setIsLoading(false);
      setCustomQuery(''); // Clear the input field after search attempt
    }
  };

  // Function to clear all stored sources
  const handleClearSources = () => {
    log('Clearing all sources');
    setSuggestedSources([]);
    setSearchQueries([]);
    setRejectedBelowThreshold(0);
  };

  const isSourceSelected = (source) => {
    if (!Array.isArray(sources) || sources.length === 0 || !source?.id) return false;
    if (!multiSelect) return sources[0]?.id === source.id;
    return sources.some((item) => String(item?.id) === String(source.id));
  };

  const handleToggleSource = async (source) => {
    const selected = isSourceSelected(source);
    if (multiSelect) {
      if (!selected && sources.length >= maxSources) return;
      await onSourceSelect(source, !selected);
      return;
    }
    await onSourceSelect(source);
  };

  // Add console log to verify rendering logic
  console.log('Rendering suggested sources:', suggestedSources);

  // Calculate pagination
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;
  // Sources that cleared their own provider's bar. If none did, show everything
  // rather than leaving the step empty — the score column tells the real story.
  const passingSources = suggestedSources.filter((source) => !source.belowThreshold);
  const visibleSources = showWeakMatches
    ? suggestedSources
    : (passingSources.length > 0 ? passingSources : suggestedSources);
  const weakMatchCount = suggestedSources.length - passingSources.length;
  const paginatedSources = visibleSources.slice(startIndex, endIndex);
  const totalPages = Math.ceil(visibleSources.length / resultsPerPage);

  // Ensure component structure is set up to display sources
  return (
    <div className="mt-6">
      {!hideHeader && (
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-700">Scientific Sources</h3>
            <p className="text-sm text-gray-500">
              Search for scientific articles and research papers to support your content.
            </p>
          </div>
          {!hideCloseButton && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      )}

      {/* Tabs: our suggestions vs. the workspace's own documents */}
      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {[
          { id: 'recommendations', label: t('articles.create.articleSources.tabRecommendations', 'Rekomendacje'), count: visibleSources.length },
          { id: 'mySources', label: t('articles.create.articleSources.tabMySources', 'Moje źródła'), count: vectorStoreFiles.length }
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.count > 0 && <span className="ml-1.5 text-xs text-gray-400">({tab.count})</span>}
          </button>
        ))}
      </div>

      {activeTab === 'mySources' && vectorStoreFiles.length > 0 && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">
              {t('articles.create.articleSources.workspaceDocuments', 'Workspace documents')}
              {' '}({vectorStoreFiles.length})
            </h4>
            {vectorStoreFiles.length > 5 && (
              <input
                type="text"
                value={vectorStoreFilter}
                onChange={(e) => setVectorStoreFilter(e.target.value)}
                placeholder={t('articles.create.articleSources.filterDocuments', 'Filter documents')}
                className="text-xs px-2 py-1 border border-gray-200 rounded"
              />
            )}
          </div>
          <div className="max-h-48 overflow-y-auto space-y-1">
            {vectorStoreFiles
              .filter((file) => !vectorStoreFilter
                || String(file.filename).toLowerCase().includes(vectorStoreFilter.toLowerCase()))
              .map((file) => {
                const asSource = {
                  id: file.id,
                  file_id: file.id,
                  filename: file.filename,
                  title: file.filename,
                  source: t('articles.create.articleSources.workspaceDocuments', 'Workspace documents'),
                  type: 'vector_store_file',
                  apiSource: 'vector_store'
                };
                const isSelected = sources.some((s) => s.id === file.id || s.file_id === file.id);
                return (
                  <label key={file.id} className="flex items-center text-sm py-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSourceSelect(asSource, !isSelected)}
                      disabled={!isSelected && multiSelect && sources.length >= maxSources}
                      className="mr-2"
                    />
                    <BookOpenIcon className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{file.filename}</span>
                  </label>
                );
              })}
          </div>
        </div>
      )}

      {/* API Sources Selection - every searchable dataset is queried by default,
          so the per-source toggles are a localhost debugging affordance. */}
      {isLocalhost() && availableApiSources.filter(source => source.enabled).length > 1 && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Search Sources</h4>
          <div className="grid grid-cols-2 gap-2">
            {availableApiSources.filter(source => source.enabled).map(apiSource => (
              <label key={apiSource.id} className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={selectedApiSources.includes(apiSource.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedApiSources(prev => [...prev, apiSource.id]);
                    } else {
                      setSelectedApiSources(prev => prev.filter(id => id !== apiSource.id));
                    }
                  }}
                  className="mr-2"
                />
                <div>
                  <div className="font-medium">{apiSource.name}</div>
                  <div className="text-xs text-gray-500">{apiSource.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Custom search input */}
      <div className="mb-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={customQuery}
            onChange={(e) => setCustomQuery(e.target.value)}
            placeholder="Add custom search query (e.g., 'diabetes treatment', 'machine learning')"
            className="flex-1 rounded-lg border-gray-300 bg-white text-sm"
            onKeyPress={(e) => e.key === 'Enter' && handleCustomSearch()}
          />
          <button
            onClick={handleCustomSearch}
            disabled={!customQuery.trim() || isLoading}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-300"
          >
            <MagnifyingGlassIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {isLoading && (
        <div className="flex items-center justify-center ">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <span className="ml-2 text-gray-500">
            {t('articles.create.articleSources.searching', 'Searching workspace documents and selected sources...')}
          </span>
        </div>
      )}
      
      {error && (
        <div className="p-4 bg-red-50 text-red-500 rounded-lg mb-4">
          <div className="font-medium">Error retrieving sources</div>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}



      {/* Web search is the last tier: offered only once workspace sources have run. */}
      {!isLoading && (
        <div className="mb-4">
          <button
            type="button"
            onClick={handleWebSearch}
            disabled={isWebSearching}
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <MagnifyingGlassIcon className="w-4 h-4" />
            {isWebSearching
              ? t('articles.create.articleSources.webSearching', 'Searching the web...')
              : t('articles.create.articleSources.searchWeb', 'Search verified websites')}
          </button>
          {webSearchError && (
            <p className="text-xs text-red-600 mt-1">{webSearchError}</p>
          )}
        </div>
      )}

      {activeTab === 'recommendations' && !isLoading && !error && suggestedSources.length > 0 && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              {t('articles.create.articleSources.foundCount', { count: visibleSources.length })}
              {weakMatchCount > 0 ? ` • ${t('articles.create.articleSources.weakMatches', { count: weakMatchCount })}` : ''}
            </p>
            <div className="flex items-center gap-4">
              {weakMatchCount > 0 && (
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={showWeakMatches}
                    onChange={(e) => { setShowWeakMatches(e.target.checked); setCurrentPage(1); }}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                  />
                  {t('articles.create.articleSources.showWeak', 'Pokaż słabe dopasowania')}
                </label>
              )}
              <button
                onClick={handleClearSources}
                className="text-sm text-red-600 hover:text-red-800"
              >
                {t('articles.create.articleSources.clearAll', 'Wyczyść')}
              </button>
            </div>
          </div>

          {/* Display paginated sources */}
          {layout === 'table' ? (
            <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="overflow-x-auto">
                <table className="min-w-[760px] w-full table-fixed divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-[46%] py-3 pl-4 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {t('articles.create.articleSources.table.source')}
                      </th>
                      <th className="w-[26%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {t('articles.create.articleSources.table.authors')}
                      </th>
                      <th className="w-[14%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {t('articles.create.articleSources.table.year')}
                      </th>
                      <th className="w-[14%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {t('articles.create.articleSources.table.score')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {paginatedSources.map((source, sourceIndex) => {
                      const isSelected = isSourceSelected(source);
                      const isDisabled = multiSelect && !isSelected && sources.length >= maxSources;
                      return (
                        <tr
                          key={`${currentPage}-${source.id || sourceIndex}`}
                          onClick={() => !isDisabled && handleToggleSource(source)}
                          className={`transition-colors ${
                            isSelected
                              ? 'bg-gray-50 cursor-pointer'
                              : (isDisabled ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer')
                          }`}
                        >
                          <td className="px-4 py-3 align-middle">
                            <div className="flex min-w-0 items-start gap-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={() => handleToggleSource(source)}
                                onClick={(e) => e.stopPropagation()}
                                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                              />
                              <div className="min-w-0 flex-1">
                                <p className="whitespace-normal break-words text-sm font-medium text-gray-900">{source.title}</p>
                                {source.source && (
                                  <span className={`mt-1 inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${
                                    source.type === 'vector_store_file'
                                      ? 'bg-primary-50 text-primary-700'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {source.source}
                                  </span>
                                )}
                                {source.journal && (
                                  <p className="mt-1 text-xs text-gray-500">{source.journal}</p>
                                )}
                                {source.url && (
                                  <a
                                    href={source.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="mt-1 inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800"
                                  >
                                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                    {source.id || 'link'}
                                  </a>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 align-middle text-sm text-gray-700">
                            {source.authors || '—'}
                          </td>
                          <td className="px-3 py-3 align-middle text-sm text-gray-700">
                            {source.pubDate || '—'}
                          </td>
                          <td className="px-3 py-3 align-middle">
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                              Number(source.relevanceScore) >= 0.85
                                ? 'bg-green-50 text-green-800'
                                : (source.belowThreshold ? 'bg-amber-50 text-amber-800' : 'bg-gray-100 text-gray-800')
                            }`}>
                              {formatRelevanceScore(source.relevanceScore)}
                            </span>
                            {source.belowThreshold && (
                              <span className="block text-[11px] text-gray-400 mt-1">
                                {t('articles.create.articleSources.weakMatch', 'weak match')}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
          <div className="space-y-3 mt-4">
            {paginatedSources.map((source, sourceIndex) => {
              const isSelected = isSourceSelected(source);
              
              return (
                <div
                  key={`${currentPage}-${sourceIndex}`}
                  className={`p-3 bg-white border rounded-lg transition-colors ${
                    isSelected 
                      ? 'border-primary-500 bg-primary-50' 
                      : 'border-gray-200 hover:border-primary-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className="flex items-center justify-center w-5 h-5 mt-1 flex-shrink-0 cursor-pointer"
                      onClick={async () => await handleToggleSource(source)}
                    >
                      {isSelected ? (
                        <CheckCircleIcon className="w-5 h-5 text-primary-500" />
                      ) : (
                        <BookOpenIcon className="w-5 h-5 text-gray-400 hover:text-primary-500" />
                      )}
                    </div>
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={async () => await handleToggleSource(source)}
                    >
                      <h5 className="font-medium text-gray-900 text-sm line-clamp-2">
                        {source.title}
                      </h5>
                      <p className="text-xs text-gray-600 mt-1">
                        {source.authors} • {source.journal} • {source.pubDate}
                      </p>
                      {source.doi && (
                        <p className="text-xs text-gray-500 mt-1">
                          DOI: {source.doi}
                        </p>
                      )}
                      <p className="text-xs text-primary-600 mt-1">
                        {source.source} - {source.id}
                        {source.apiSource && source.apiSource !== 'ncbi' && (
                          <span className="chip-api-source">
                            {source.apiSource.toUpperCase()}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex-shrink-0 ml-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSourceForDrawer(source);
                          setDrawerOpen(true);
                        }}
                        className="inline-flex items-center p-1 border border-transparent rounded-full text-gray-400 hover:text-primary-500 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        title="View source details"
                      >
                        <InformationCircleIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          )}

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-sm border border-gray-300 rounded ${
                          currentPage === pageNum 
                            ? 'bg-primary-500 text-white border-primary-500' 
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              
              <p className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </p>
            </div>
          )}
        </div>
      )}

      {!isLoading && !error && suggestedSources.length === 0 && articleContext && rejectedBelowThreshold === 0 && (
        <div className="p-4 bg-gray-50 text-gray-500 rounded-lg">
          No scientific sources found. Try with a more specific article context or use custom search terms.
          {isLocalhost() && (
            <div className="text-xs mt-2">
              Debug: searchAttempt: {searchAttemptRef.current}, 
              triggerSearch: {triggerSearch}, 
              articleContext length: {articleContext.length},
              articleContext: "{articleContext.substring(0, 30)}...",
              searchQueries: {searchQueries.length}
            </div>
          )}
        </div>
      )}

      {/* Animated Drawer with higher z-index */}
      <div className={`fixed inset-0 z-[60] transition-opacity duration-300 ease-in-out ${drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setDrawerOpen(false)}></div>
        <div className={`fixed right-0 top-0 h-full w-96 bg-white shadow-xl overflow-y-auto transform transition-transform duration-300 ease-in-out ${drawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          {selectedSourceForDrawer && (
            <>
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Source Details</h3>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4">
                {/* Title */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    {selectedSourceForDrawer.title}
                  </h4>
                </div>

                {/* Metadata */}
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Authors:</span>
                    <p className="text-gray-600 mt-1">{selectedSourceForDrawer.authors}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Journal:</span>
                    <p className="text-gray-600 mt-1">{selectedSourceForDrawer.journal}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Publication Date:</span>
                    <p className="text-gray-600 mt-1">{selectedSourceForDrawer.pubDate}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Source:</span>
                    <p className="text-gray-600 mt-1">{selectedSourceForDrawer.source} - {selectedSourceForDrawer.id}</p>
                  </div>
                  {selectedSourceForDrawer.doi && (
                    <div>
                      <span className="font-medium text-gray-700">DOI:</span>
                      <p className="text-gray-600 mt-1">{selectedSourceForDrawer.doi}</p>
                    </div>
                  )}
                </div>

                {/* Abstract */}
                {selectedSourceForDrawer.abstract && selectedSourceForDrawer.abstract !== 'Abstract not available' && (
                  <div>
                    <span className="font-medium text-gray-700">Abstract:</span>
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-700 leading-relaxed">
                      {selectedSourceForDrawer.abstract}
                    </div>
                  </div>
                )}

                {/* Full Text Excerpt */}
                {selectedSourceForDrawer.fullText && (
                  <div>
                    <span className="font-medium text-gray-700">Content Excerpt:</span>
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-700 leading-relaxed max-h-40 overflow-y-auto">
                      {selectedSourceForDrawer.fullText}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  {selectedSourceForDrawer.url && (
                    <a
                      href={selectedSourceForDrawer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-800 font-medium"
                    >
                      <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      View Full Article
                    </a>
                  )}
                  
                  {isSourceSelected(selectedSourceForDrawer) ? (
                    <div>
                      <button
                        onClick={async () => {
                          await handleToggleSource(selectedSourceForDrawer);
                          setDrawerOpen(false);
                        }}
                        className="w-full bg-gray-100 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        {multiSelect ? 'Remove This Source' : 'Currently Selected'}
                      </button>
                    </div>
                  ) : (
                    <div>
                      <button
                        onClick={async () => {
                          await handleToggleSource(selectedSourceForDrawer);
                          setDrawerOpen(false);
                        }}
                        disabled={multiSelect && sources.length >= maxSources}
                        className="w-full bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-300"
                      >
                        Select This Source
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SourcesSearch; 